import type { PortableTextComponents } from '@portabletext/react'
import { ReactNode, useRef, useState } from 'react'
import useSWRImmutable from 'swr/immutable'

import BEMHelper from '@/helpers/bem'
import docToUrl from '@/helpers/docToUrl'
import { byline, hasLongWord } from '@/helpers/utils'

import useResize from '@/hooks/useResize'
import Image from '../Image'
import RichText from '../RichText'
import InlineReference from '../InlineReference'
import Link from '../Link'
import { List, ListItem } from '../List'
import styles from './Styles.module.scss'

const bem = BEMHelper(styles)

function Factbox({ value: { title, content } }: { value: { title: string; content: string } }) {
  const factboxRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  useResize(() => {
    setTimeout(() => {
      if (factboxRef.current) {
        setHeight(factboxRef.current.clientHeight)
      }
    }, 100)
  }, [])

  return (
    <aside {...bem('factbox')} ref={factboxRef} style={{ '--height': height + 'px' }}>
      <h3>{title}</h3>
      <p>{content}</p>
    </aside>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function RenderImage({ value }: { value: any }) {
  return <Image {...value} {...bem('image', value.type)} alt={''} />
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Gallery({ value }: { value: any }) {
  const images = value?.images || []
  const caption = value?.caption

  return (
    <figure {...bem('gallery')}>
      <div {...bem('images')}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {images.map(({ _key, ...image }: { _key: string; [key: string]: any }) => (
          <Image key={_key} {...image} alt={''} />
        ))}
      </div>
      {caption && <figcaption {...bem('caption')}>{caption}</figcaption>}
    </figure>
  )
}

const fetcher = (...args: [RequestInfo, RequestInit?]) => fetch(...args).then((res) => res.json())

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function MicrofactsMark(props: any) {
  const id = props.value?.collectionId
  const lang = props.value?.lang
  const { data, error } = useSWRImmutable(
    id && lang ? `/api/factiverse/microfact-by-id?id=${id}&lang=${lang}` : null,
    fetcher,
    {},
  )
  let content = ''
  let sourceUrl = ''
  if (!data && !error) {
    content = 'Laster…'
  } else if (data) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const spot = data.spots?.find((spot: any) => spot.entity._id === props.value.entityId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const kbRef = spot?.entity?.kbs_refs?.find((kbRef: any) => kbRef.kb_id === props.value.kb_id)
    if (kbRef) {
      content = kbRef.description
      sourceUrl = kbRef.pageUrl
    } else {
      content = ''
    }
  }
  return (
    <InlineReference
      mark={{
        content: content,
      }}
      sourceUrl={sourceUrl}
    >
      {props.children}
    </InlineReference>
  )
}

const serializers: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p {...bem('text')}>{children}</p>,
    h2: ({ children }) => (
      <>
        <span {...bem('separator')} />
        <h2 {...bem('sub-title')}>{children}</h2>
      </>
    ),
    preamble: ({ children }) => <p {...bem('preamble')}>{children}</p>,
  },
  list: ({ value, children }) => (
    <List type={value.listItem === 'bullet' ? 'ul' : 'ol'} level={value.level} {...bem('list')}>
      {children}
    </List>
  ),
  listItem: ({ children }) => <ListItem>{children}</ListItem>,
  types: {
    factbox: Factbox,
    image: RenderImage,
    gallery: Gallery,
  },
  marks: {
    internalLink: ({ value, children }) => <Link href={docToUrl(value?.target)}>{children}</Link>,
    link: ({ value, children }) => <Link href={value?.href || '#'}>{children}</Link>,
    definition: ({ value, children }) => <InlineReference mark={value}>{children}</InlineReference>,
    microfacts: MicrofactsMark,
  },
}

interface ArticleProps {
  title: string
  preamble?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  image?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  text?: any[]
  type?: string
  children?: ReactNode
  publishedAt?: string
  updatedAt?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  author?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loanArticle?: any
}

export default function Article({
  title,
  preamble,
  image,
  text,
  type,
  children,
  ...props
}: ArticleProps) {
  const updated = byline(props)
  const portrait = image?.dimensions?.aspectRatio < 1
  const smallTitle = hasLongWord(title, 15)

  const isArticle = type !== 'page'

  return (
    <article {...bem('')}>
      <header {...bem('header', { image, portrait })}>
        <div {...bem('header-content')}>
          <h1 {...bem('title', { small: portrait && smallTitle })}>{title}</h1>
          {isArticle && <span {...bem('byline')}>{updated}</span>}
        </div>
        {image && <Image {...bem('main-image')} {...image} alt={''} />}
      </header>

      {preamble && <p {...bem('preamble')}>{preamble}</p>}

      {text && <RichText {...bem('content')} blocks={text} components={serializers} />}

      {children}
    </article>
  )
}
